import DOMPurify from 'dompurify';

/**
 * HTML Sanitization Utilities
 *
 * Provides secure HTML sanitization to prevent XSS attacks.
 * All user-generated content should be sanitized before rendering.
 *
 * @module utils/sanitize
 */

/**
 * Sanitizes HTML content to prevent XSS attacks
 *
 * @param {string} dirty - Unsanitized HTML string
 * @param {object} options - DOMPurify configuration options
 * @returns {string} - Sanitized HTML string safe for rendering
 *
 * @example
 * const clean = sanitizeHTML('<p>Hello <script>alert("XSS")</script></p>');
 * // Returns: '<p>Hello </p>'
 */
export const sanitizeHTML = (dirty, options = {}) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  const defaultConfig = {
    ALLOWED_TAGS: [
      'b', 'i', 'u', 'strong', 'em', 'br', 'p',
      'ul', 'ol', 'li', 'a', 'span', 'div'
    ],
    ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
    ALLOW_DATA_ATTR: false,
    // Prevent JavaScript URLs
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  };

  const config = { ...defaultConfig, ...options };

  return DOMPurify.sanitize(dirty, config);
};

/**
 * Sanitizes HTML for rich text content (more permissive)
 *
 * Use for report blocks and rich text editors where users need
 * more formatting options.
 *
 * @param {string} dirty - Unsanitized HTML string
 * @returns {string} - Sanitized HTML string
 *
 * @example
 * const clean = sanitizeRichText('<h1>Title</h1><p>Content</p>');
 * // Preserves headings, paragraphs, and formatting
 */
export const sanitizeRichText = (dirty) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  return sanitizeHTML(dirty, {
    ALLOWED_TAGS: [
      'b', 'i', 'u', 'strong', 'em', 'br', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'span', 'div', 'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td'
    ],
    ALLOWED_ATTR: ['href', 'target', 'class', 'style', 'rel', 'id'],
  });
};

/**
 * Sanitizes HTML for simple messages (most restrictive)
 *
 * Use for comments, chat messages, and user-submitted text where
 * only basic formatting is needed.
 *
 * @param {string} dirty - Unsanitized HTML string
 * @returns {string} - Sanitized HTML string
 *
 * @example
 * const clean = sanitizeMessage('<p>Hello <b>world</b>!</p>');
 * // Allows only basic formatting
 */
export const sanitizeMessage = (dirty) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  return sanitizeHTML(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
};

/**
 * Strips all HTML tags, returning plain text only
 *
 * Use when no HTML formatting should be allowed.
 *
 * @param {string} html - HTML string
 * @returns {string} - Plain text with all tags removed
 *
 * @example
 * const text = stripHTML('<p>Hello <b>world</b>!</p>');
 * // Returns: 'Hello world!'
 */
export const stripHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
};

/**
 * Sanitizes HTML attributes only
 *
 * Use for cleaning attributes like style, class, etc.
 *
 * @param {string} attributeValue - Attribute value to sanitize
 * @returns {string} - Sanitized attribute value
 */
export const sanitizeAttribute = (attributeValue) => {
  if (!attributeValue || typeof attributeValue !== 'string') {
    return '';
  }

  // Remove any JavaScript-related content
  return attributeValue
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/data:/gi, '')
    .replace(/vbscript:/gi, '');
};

/**
 * Validates that a URL is safe for redirection (relative or same-origin)
 *
 * Prevents open redirect vulnerabilities by ensuring URLs are either:
 * - Relative paths (start with /)
 * - Same-origin absolute URLs
 *
 * @param {string} url - URL to validate
 * @param {boolean} allowExternal - Allow external URLs (default: false)
 * @returns {boolean} - True if URL is safe for redirect
 *
 * @example
 * isValidRedirect('/home'); // true
 * isValidRedirect('https://evil.com'); // false
 * isValidRedirect('javascript:alert(1)'); // false
 */
export const isValidRedirect = (url, allowExternal = false) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Block javascript:, data:, vbscript: and other dangerous protocols
  const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i;
  if (dangerousProtocols.test(url.trim())) {
    return false;
  }

  // Allow relative paths
  if (url.startsWith('/')) {
    return true;
  }

  // For absolute URLs, validate same-origin or allow if explicitly permitted
  if (url.startsWith('http://') || url.startsWith('https://')) {
    if (allowExternal) {
      return true;
    }

    try {
      const urlObj = new URL(url);
      const currentOrigin = window.location.origin;
      return urlObj.origin === currentOrigin;
    } catch (e) {
      return false;
    }
  }

  // Block anything else (e.g., protocol-relative URLs like //evil.com)
  return false;
};

/**
 * Sanitizes a redirect URL to ensure it's safe
 *
 * Returns the URL if valid, otherwise returns a safe fallback
 *
 * @param {string} url - URL to sanitize
 * @param {string} fallback - Fallback URL if invalid (default: '/home')
 * @param {boolean} allowExternal - Allow external URLs (default: false)
 * @returns {string} - Safe redirect URL
 *
 * @example
 * sanitizeRedirect('/dashboard'); // '/dashboard'
 * sanitizeRedirect('https://evil.com'); // '/home'
 */
export const sanitizeRedirect = (url, fallback = '/home', allowExternal = false) => {
  if (isValidRedirect(url, allowExternal)) {
    return url;
  }
  console.warn('Invalid redirect URL blocked:', url);
  return fallback;
};

/**
 * Configuration preset for different sanitization levels
 */
export const SANITIZE_PRESETS = {
  STRICT: {
    ALLOWED_TAGS: ['b', 'i', 'u', 'em', 'strong'],
    ALLOWED_ATTR: [],
  },
  BASIC: {
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'a'],
    ALLOWED_ATTR: ['href', 'target'],
  },
  RICH: {
    ALLOWED_TAGS: [
      'b', 'i', 'u', 'strong', 'em', 'br', 'p', 'h1', 'h2', 'h3', 'h4',
      'ul', 'ol', 'li', 'a', 'span', 'div', 'blockquote', 'code', 'pre'
    ],
    ALLOWED_ATTR: ['href', 'target', 'class', 'style', 'rel'],
  },
};

// Default export for convenience
const sanitizeUtils = {
  sanitizeHTML,
  sanitizeRichText,
  sanitizeMessage,
  stripHTML,
  sanitizeAttribute,
  isValidRedirect,
  sanitizeRedirect,
  SANITIZE_PRESETS,
};

export default sanitizeUtils;
